import { z } from 'zod';
import type { Actor } from './actor';
import { assertNever } from './exhaustive';
import {
  COLUMN_DATE_FORMATS,
  COLUMN_DECIMAL_SEPARATORS,
  COLUMN_THOUSANDS_SEPARATORS,
  DATETIME_OFFSET_PATTERN,
  DEFAULT_COLUMN_FORMAT,
  type FieldType,
} from './field-types';
import { parseRecordQuery, type RecordQuery } from './record-query';

/**
 * THE CSV MODULE'S VOCABULARY (`docs/decisions/csv-import-export.md`).
 *
 * Bulk ingestion of record data from a file, and bulk extraction to one. This file is
 * what the three consumers have to agree on (INV-11): the server parses, coerces, writes
 * and serializes; the browser drives the wizard and renders the run; and migration 0014
 * creates the columns these enums are the domain of. A status spelled one way in a route
 * and another way in a CHECK constraint is a run that can never finish.
 *
 * WHAT IS DELIBERATELY NOT HERE: a CSV PARSER. `shared` ships source with no build step
 * (INV-11) and the browser never parses for the write (D1), so the parser is a
 * `packages/server` dependency and this file holds only what both sides read - limits,
 * ladders, outcome vocabularies, the injection armor and the wire schemas.
 *
 * The two per-field members the module added to the field-type registry, `fromText` and
 * `toText`, live in `field-types.ts` beside `valueSchema` (D8, D37), because INV-12's
 * exhaustiveness check is what makes a thirteenth field type impossible to forget.
 */

// --- the file (D2, D5) ------------------------------------------------------

/**
 * SNIFFED ACROSS THESE THREE, THEN SHOWN AND OVERRIDABLE (D2). Not decoration: the
 * organization is German and Excel in a de-DE locale writes semicolon-delimited CSV, so a
 * comma-only importer rejects the most common file this instance will ever see.
 */
export const CSV_DELIMITERS = [',', ';', '\t'] as const;
export type CsvDelimiter = (typeof CSV_DELIMITERS)[number];

export const CSV_DEFAULT_DELIMITER: CsvDelimiter = ',';

export function isCsvDelimiter(value: string): value is CsvDelimiter {
  return (CSV_DELIMITERS as readonly string[]).includes(value);
}

/** Stripped from the first header only. Bytes that fail UTF-8 fail the run (D2). */
export const CSV_BYTE_ORDER_MARK = '﻿';

/**
 * D5, sized from this repository rather than from HubSpot's price list: 2,000 rows is the
 * yardstick three modules were built against, a one-time migration for a 5-to-15-person
 * organization is plausibly ten to twenty thousand rows, and 200 columns is double the
 * widest seeded object.
 *
 * EACH IS A SEPARATE NAMED REFUSAL AT UPLOAD, with the actual number in the message.
 * Arif's answer to question 3 on 2026-08-04: ship these, measure the first real import,
 * adjust. Honesty register item 1: the row cap rests on an UNMEASURED throughput estimate
 * and must be re-derived from a real measurement before it is treated as a promise.
 */
export const CSV_MAX_FILE_BYTES = 20 * 1024 * 1024;
export const CSV_MAX_ROWS = 50_000;
export const CSV_MAX_COLUMNS = 200;

/**
 * D11's display cap. Above it a column is not aggregated and errors are reported per row
 * instead, because 50,000 rows can hold 50,000 distinct emails and listing them is a
 * second copy of the file rather than a review.
 *
 * It is also what bounds ONE value-fix request (D43), which is why it lives here and not
 * in the server's config: the payload schema below is written against it.
 */
export const IMPORT_REVIEW_DISTINCT_VALUE_CAP = 500;

/** D32: a selection narrows an export; above this the operator filters instead. */
export const EXPORT_SELECTION_MAX = 1_000;

/**
 * D34: the stored export file lives this long, then the BYTES are nulled and the row
 * survives as the audit record (D38). Shorter than Notion's link expiry (7 days would tie)
 * and HubSpot's thirty, because these are customer records in our own database. Adopted
 * per recommendation on question 4 - Arif has not confirmed it, and it is reversible.
 */
export const EXPORT_FILE_RETENTION_DAYS = 7;

/**
 * HOW MANY FILES ONE BATCH MAY CARRY (D45: one batch, several files, several objects, the
 * reference pass at the end).
 *
 * Sized from what a first migration is, not from a storage limit: the widest seeded
 * instance has fewer than twenty objects, and a batch is one file per object. Each file
 * still pays D5's own caps on its own, so a batch at this cap is twenty separate refusals
 * rather than one large one.
 */
export const IMPORT_BATCH_MAX_FILES = 25;

// --- injection armor (D36) --------------------------------------------------

/**
 * ARMOR IS ON, WITH NO TOGGLE, AND THE IMPORTER STRIPS ITS OWN MARKER (D36).
 *
 * Attio ships the same protection as an admin setting that is off by default, and its own
 * warning is the argument against its design: enabling it "changes the format of text
 * field values in exported files". A setting that silently changes every exported text
 * cell is a support incident waiting for a new employee. Twenty's resolution is better and
 * is the one adopted - prefix, then strip on re-import, so the round trip is lossless and
 * the trade-off Attio documents stops being a trade-off.
 *
 * The marker is a zero-width joiner rather than an apostrophe because Excel RENDERS the
 * apostrophe and the joiner is invisible.
 */
export const CSV_ARMOR_MARKER = '‍';

/**
 * A named constant with its own test rather than an inline regex (D36). Carriage return
 * and tab are in the set because a leading one lets a cell continue into what a
 * spreadsheet reads as a new field.
 */
export const CSV_DANGEROUS_FIRST_CHARACTERS = ['=', '+', '-', '@', '\t', '\r'] as const;

/**
 * ARMOR APPLIES ONLY TO TEXT-FAMILY OUTPUT (D36). NUMBER, CURRENCY, DATE, DATETIME and
 * BOOLEAN come out of a closed grammar (D37) that cannot produce a formula, so a canonical
 * `-2100` from a NUMBER column is not user-authored content and does not get a marker.
 * Twenty prefixes on the character alone, which makes a negative number read as text in
 * Excel; the narrower target set is the whole improvement.
 */
export const ARMORED_FIELD_TYPES = [
  'TEXT',
  'EMAIL',
  'URL',
  'PHONE',
  'SELECT',
  'MULTI_SELECT',
  /**
   * FILES (`file-attachments.md` D11, D12). A list rather than a map, so a thirteenth type
   * joins it silently or not at all - and this one has to join it: the exported column is
   * user-authored FILENAMES, and a filename may begin with `=`.
   */
  'FILES',
] as const satisfies readonly FieldType[];

export function armorsExportText(fieldType: FieldType): boolean {
  return (ARMORED_FIELD_TYPES as readonly FieldType[]).includes(fieldType);
}

function needsCsvArmor(value: string): boolean {
  const first = value.slice(0, 1);
  if (first === '') return false;
  /**
   * A value that ALREADY begins with the marker is armored too, so that stripping one
   * marker on re-import restores it. Without this branch `stripCsvArmor(armorCsvText(v))`
   * is the identity for every string except the ones that happen to start with a joiner,
   * and a round-trip property with an exception is not a property.
   */
  return (
    first === CSV_ARMOR_MARKER ||
    (CSV_DANGEROUS_FIRST_CHARACTERS as readonly string[]).includes(first)
  );
}

export function armorCsvText(value: string): string {
  return needsCsvArmor(value) ? `${CSV_ARMOR_MARKER}${value}` : value;
}

/** Strips a SINGLE leading marker from every cell, before coercion (D36). */
export function stripCsvArmor(value: string): string {
  return value.startsWith(CSV_ARMOR_MARKER) ? value.slice(CSV_ARMOR_MARKER.length) : value;
}

// --- runs: kinds, statuses, the ladder (D12, D29, D30, D41, D44) ------------

/**
 * ONE TABLE, TWO KINDS (D44). A revert is a run in `meta.import_run` rather than a
 * mechanism of its own, so D30's object lock, D41's CAS and Retry, and D42's queue shape
 * all cover it with no new machinery - and an import while a revert runs is refused by the
 * same index that refuses a revert while an import runs.
 */
export const IMPORT_RUN_KINDS = ['import', 'revert'] as const;
export type ImportRunKind = (typeof IMPORT_RUN_KINDS)[number];

/**
 * `draft -> ready -> queued -> running -> terminal` (D12, D30).
 *
 * `queued` is the one to understand. It is written SYNCHRONOUSLY by `POST .../run` and
 * `POST .../revert`, inside the transaction that checks the object, which is what makes
 * the 409 real: a refusal the caller is told about needs a write the caller is waiting on.
 * An earlier draft left the transition to `running` in the worker and then claimed a 409
 * "from the constraint" - the safety property held and the stated experience did not exist.
 *
 * `completed` means the job finished, NOT that every row succeeded; the counters say the
 * rest.
 */
export const IMPORT_RUN_STATUSES = [
  'draft',
  'validating',
  'ready',
  'queued',
  'running',
  'completed',
  'failed',
  'canceled',
] as const;
export type ImportRunStatus = (typeof IMPORT_RUN_STATUSES)[number];

/**
 * WHAT HOLDS THE OBJECT (D30). The partial unique index covers exactly these two, because
 * `queued` is now a state the object is held in and not merely a note about a job.
 */
export const IMPORT_RUN_ACTIVE_STATUSES: readonly ImportRunStatus[] = ['queued', 'running'];

export const IMPORT_RUN_TERMINAL_STATUSES: readonly ImportRunStatus[] = [
  'completed',
  'failed',
  'canceled',
];

export function holdsTheObject(status: ImportRunStatus): boolean {
  return IMPORT_RUN_ACTIVE_STATUSES.includes(status);
}

export function isTerminalImportRunStatus(status: ImportRunStatus): boolean {
  return IMPORT_RUN_TERMINAL_STATUSES.includes(status);
}

/**
 * THE LADDER AS DATA, so the route guards and the worker cannot disagree about it.
 *
 * Every edge is stated somewhere: `draft -> validating -> ready` is the wizard (D29),
 * `ready -> queued` is the CAS both `POST .../run` and `POST .../revert` perform (D30),
 * `queued -> running` is the handler's own CAS (D41), and `failed -> queued` plus
 * expired-`running -> queued` are the two statuses the operator Retry route accepts (D41).
 * Cancel writes `canceled` from anywhere a run is not already terminal, because it must
 * work on a stuck run by definition - it writes the status without needing a handler.
 */
export const IMPORT_RUN_LADDER: Readonly<Record<ImportRunStatus, readonly ImportRunStatus[]>> = {
  draft: ['validating', 'ready', 'canceled'],
  validating: ['ready', 'draft', 'failed', 'canceled'],
  ready: ['queued', 'canceled'],
  queued: ['running', 'failed', 'canceled'],
  running: ['completed', 'failed', 'canceled', 'queued'],
  completed: [],
  failed: ['queued'],
  canceled: [],
};

export function canFollowImportRunStatus(from: ImportRunStatus, to: ImportRunStatus): boolean {
  return IMPORT_RUN_LADDER[from].includes(to);
}

/**
 * EXPORT RUNS DO NOT HAVE A WIZARD (D34, D42). `POST /api/exports` creates a run and
 * enqueues it in one call, so there is no draft to configure and no `ready` to sit in; the
 * CAS, the Retry route and the `/ready` stuck-run line are otherwise the import's, with
 * `CSV_EXPORT_EXPIRE_SECONDS` as the clock.
 */
export const EXPORT_RUN_STATUSES = ['queued', 'running', 'completed', 'failed'] as const;
export type ExportRunStatus = (typeof EXPORT_RUN_STATUSES)[number];

// --- modes and the match key (D14, D15) -------------------------------------

/**
 * HubSpot's three, verbatim in behaviour (D15). Airtable's missing update-only mode is the
 * gap that matters: a corrections file run as upsert against a mis-typed key silently
 * CREATES records instead of updating them, and D44's revert covers only the records a run
 * created - a wrongly-created record is reversible, a wrongly-updated one is not.
 */
export const IMPORT_MODES = ['create', 'update', 'upsert'] as const;
export type ImportMode = (typeof IMPORT_MODES)[number];

/**
 * D14 made this structural rather than a warning: `create` needs no key, and `update` and
 * `upsert` cannot START without one. Attio warns and proceeds, which produces duplicates
 * by design; there is no state here in which the operator asked for matching and is not
 * matching.
 */
export function importModeNeedsMatchKey(mode: ImportMode): boolean {
  switch (mode) {
    case 'create':
      return false;
    case 'update':
    case 'upsert':
      return true;
    default:
      return assertNever(mode, 'importModeNeedsMatchKey');
  }
}

/**
 * EXACTLY THREE SOURCES (D14), and no precedence ladder, because there is only ever one
 * key per run. `id` is UPDATE-ONLY: a UUID matching no record is a row error and never a
 * create, since `createRecord` mints its own UUIDv7 and accepting a caller-supplied id
 * would create a record whose identity came from a spreadsheet.
 */
export const IMPORT_MATCH_KEY_SOURCES = ['identity', 'unique', 'id'] as const;
export type ImportMatchKeySource = (typeof IMPORT_MATCH_KEY_SOURCES)[number];

// --- outcomes (D12, D44) ----------------------------------------------------

/**
 * FOUR PER CELL, and the fourth has no equivalent in any researched product because none
 * of them has INV-5 (D12).
 *
 * `blocked` is neither success nor error and must never fold into either: counting it as
 * an error trains the operator to ignore errors, and counting it as success hides that
 * the number in the file is not the number in the record. It gets its own count, its own
 * colour and its own sentence (D24).
 */
export const IMPORT_CELL_OUTCOMES = ['written', 'skipped_blank', 'invalid', 'blocked'] as const;
export type ImportCellOutcome = (typeof IMPORT_CELL_OUTCOMES)[number];

/**
 * The whole domain of `meta.import_row.outcome`, both run kinds (D29). A revert run has
 * NO cell outcomes at all - it is a run over records rather than over cells - which is why
 * its three live in the same column rather than in a table of their own.
 */
export const IMPORT_ROW_OUTCOMES = [
  'created',
  'updated',
  'failed',
  'reverted',
  'skipped_touched',
  'skipped_updated',
] as const;
export type ImportRowOutcome = (typeof IMPORT_ROW_OUTCOMES)[number];

export const IMPORT_KIND_ROW_OUTCOMES: readonly ImportRowOutcome[] = [
  'created',
  'updated',
  'failed',
];

export const REVERT_KIND_ROW_OUTCOMES: readonly ImportRowOutcome[] = [
  'reverted',
  'skipped_touched',
  'skipped_updated',
];

export function rowOutcomesForKind(kind: ImportRunKind): readonly ImportRowOutcome[] {
  switch (kind) {
    case 'import':
      return IMPORT_KIND_ROW_OUTCOMES;
    case 'revert':
      return REVERT_KIND_ROW_OUTCOMES;
    default:
      return assertNever(kind, 'rowOutcomesForKind');
  }
}

/**
 * D12's row-error table, EXHAUSTIVE BY CONSTRUCTION: it enumerates every non-`one`,
 * non-`none` answer the matching stage can produce, plus the two failures that come from
 * the file and from the record layer.
 */
export const IMPORT_ROW_ERROR_CODES = [
  /** A blank key cell in a mode that needs one (D15). */
  'NO_MATCH_KEY_VALUE',
  /** Identity `ambiguous`, or several `isUnique` hits. Never a fan-out (D16). */
  'MULTI_MATCH',
  /** Another row shares the normalized key; every row in the group fails (D18). */
  'IN_FILE_DUPLICATE',
  /** The deleted-match probe hit. An import never restores and never duplicates (D19). */
  'MATCHES_DELETED_RECORD',
  /** Identity `one_stale`: matched only through a historical value (D40). */
  'STALE_IDENTITY_MATCH',
  /** Identity `unavailable`: an identifier backfill is in flight (D40). */
  'IDENTITY_UNAVAILABLE',
  /** Every mapped cell was invalid or blank (D11). */
  'ALL_VALUES_INVALID',
  /** The record layer threw, including a permission refusal (D23). */
  'WRITE_REJECTED',
] as const;
export type ImportRowErrorCode = (typeof IMPORT_ROW_ERROR_CODES)[number];

/**
 * THE SECOND PASS'S OWN REFUSALS (D9 as amended: `ONE_TO_MANY` and `MANY_TO_MANY` are
 * importable, by a stable key, in a pass that runs after every row is loaded).
 *
 * THEY ARE NOT MEMBERS OF `IMPORT_ROW_ERROR_CODES`, and that is the decision rather than
 * an oversight. That list is the domain of the row OUTCOME `failed`, and a row whose link
 * could not be resolved DID produce its record - pass one created or updated it before the
 * second pass ran. Folding these in would make `failed` mean two different things and,
 * worse, would take the record out of D44's revert set, which reads `outcome in
 * ('created','updated')`: the revert would then leave behind exactly the records a failed
 * migration most needs to take away.
 *
 * So they travel in the row's `errors` array beside the invalid-cell codes, they carry
 * their own counter on the run, and D13's artifact lists the rows holding them - which is
 * what makes "12 rows are in, and 12 links are not" a file the operator can act on.
 */
export const IMPORT_LINK_ERROR_CODES = [
  /** No record on the other object carries this key. */
  'RELATION_UNRESOLVED',
  /** Several do, through any combination of the addressable fields. Never a coin flip (D9). */
  'RELATION_AMBIGUOUS',
  /**
   * The membership exists and somebody REMOVED it. D19's rule is that an import never
   * restores, and `linkJunctionRecord` would bring it back - so the link is refused here
   * rather than resurrected by a caller that was never granted the restore verb.
   */
  'RELATION_LINK_DELETED',
  /**
   * The key resolved and the WRITE was refused: an `own`-scoped requester who may not change
   * that row (D23), a uniqueness rule on one of the junction's own fields, a child somebody
   * deleted. Its own code because `RELATION_UNRESOLVED` means one thing - no record carries
   * this key - and reporting a refused write as a missing target sends the operator looking
   * for a typo in a name that matched perfectly well. The entry carries the record layer's
   * own sentence as a third segment, so D13's artifact says which refusal it was.
   */
  'RELATION_WRITE_REFUSED',
] as const;
export type ImportLinkErrorCode = (typeof IMPORT_LINK_ERROR_CODES)[number];

/**
 * An error entry in `meta.import_row.errors` is `CODE:columnName` (D13 names the column
 * for value-level failures), so a reader that wants the CODE takes the head. One function,
 * because the artifact query and the browser both need the same split.
 */
export function importErrorCodeOf(entry: string): string {
  const colon = entry.indexOf(':');
  return colon === -1 ? entry : entry.slice(0, colon);
}

export function isImportLinkErrorCode(code: string): code is ImportLinkErrorCode {
  return (IMPORT_LINK_ERROR_CODES as readonly string[]).includes(code);
}

/**
 * THE ONE ROW ERROR THAT ALSO FAILS THE RUN (D40). `unavailable` is a property of the
 * OBJECT, not of the row, so the second row cannot succeed where the first did not, and
 * continuing would produce one identical failure per remaining row and bury the one fact
 * that matters. `last_committed_row` is what lets Retry resume once the backfill finishes.
 */
export const IMPORT_ROW_ERRORS_THAT_FAIL_THE_RUN: readonly ImportRowErrorCode[] = [
  'IDENTITY_UNAVAILABLE',
];

// --- the error artifact (D13) -----------------------------------------------

/**
 * ONE ARTIFACT: the failed rows in their ORIGINAL shape, because the workflow is
 * fix-and-re-import and a reshaped file cannot be fixed in Excel and fed back.
 *
 * Blocked values appear in it but do not make a row a failure: a row whose only
 * non-success is a blocked value SUCCEEDED, and it enters the artifact only if it also
 * failed for another reason.
 */
export const CSV_ERROR_ARTIFACT_ROW_COLUMN = '_row';
export const CSV_ERROR_ARTIFACT_ERROR_COLUMN = '_error';
export const CSV_ERROR_ARTIFACT_ERROR_SEPARATOR = ';';

// --- the actor (INV-3, D31 item 3, D44) -------------------------------------

/** `actorSchema` caps `context` at 500 characters (`actor.ts`), and this must fit. */
export const IMPORT_ACTOR_CONTEXT_MAX = 500;

export const IMPORT_ACTOR_CONTEXT_PREFIX = 'import:';
export const IMPORT_REVERT_ACTOR_CONTEXT_PREFIX = 'import-revert:';

/**
 * `import:<runId>:<filename>` (D31 item 3). The run id travels on every write, so the
 * record timeline says which file a value came from, and the events of one run are
 * findable by prefix.
 *
 * The FILENAME is what gets truncated when the cap bites, never the run id: the id is
 * what the trail is keyed on and a shortened name is still a name.
 */
export function formatImportActorContext(runId: string, filename: string): string {
  const head = `${IMPORT_ACTOR_CONTEXT_PREFIX}${runId}:`;
  return `${head}${filename.slice(0, Math.max(0, IMPORT_ACTOR_CONTEXT_MAX - head.length))}`;
}

/** The prefix that finds one run's writes, the way `workflowActorContextPrefix` does. */
export function importActorContextPrefix(runId: string): string {
  return `${IMPORT_ACTOR_CONTEXT_PREFIX}${runId}:`;
}

/**
 * `import-revert:<runId>` (D44). Not MANUAL, for the same reason the import's own writes
 * are not: a person authorised a bulk action, they did not author two thousand deletions.
 * Not SYSTEM either, because this is the import subsystem acting and the timeline should
 * say so.
 */
export function formatImportRevertActorContext(runId: string): string {
  return `${IMPORT_REVERT_ACTOR_CONTEXT_PREFIX}${runId}`;
}

/** INV-3: every row this module writes carries `IMPORT`, never the requester's MANUAL. */
export function importActor(runId: string, filename: string): Actor {
  return { source: 'IMPORT', context: formatImportActorContext(runId, filename) };
}

export function importRevertActor(runId: string): Actor {
  return { source: 'IMPORT', context: formatImportRevertActorContext(runId) };
}

// --- wire schemas (section 10, D32, D43) ------------------------------------

/**
 * ONE schema, because one enum crosses the wire as a value somebody sends: the MODE, on
 * `PATCH /api/imports/:id`. The status, kind and outcome enums are read-only projections
 * the server writes and the browser renders, so they need a TYPE and never a parser -
 * a schema nothing validates against is a promise that it is validated somewhere.
 */
export const importModeSchema = z.literal([...IMPORT_MODES]);

/**
 * The per-column declaration (D7), as it travels on `PATCH /api/imports/:id` and as it is
 * stored in `meta.import_run.formats`. Every member has a default, so a column the
 * operator never touched still arrives complete and no reader has to guess.
 */
export const columnFormatSchema = z.strictObject({
  dateFormat: z.literal([...COLUMN_DATE_FORMATS]).default(DEFAULT_COLUMN_FORMAT.dateFormat),
  decimalSeparator: z
    .literal([...COLUMN_DECIMAL_SEPARATORS])
    .default(DEFAULT_COLUMN_FORMAT.decimalSeparator),
  thousandsSeparator: z
    .literal([...COLUMN_THOUSANDS_SEPARATORS])
    .default(DEFAULT_COLUMN_FORMAT.thousandsSeparator),
  datetimeOffset: z
    .string()
    .regex(DATETIME_OFFSET_PATTERN)
    .default(DEFAULT_COLUMN_FORMAT.datetimeOffset),
  trueTokens: z
    .array(z.string().min(1))
    .min(1)
    .default([...DEFAULT_COLUMN_FORMAT.trueTokens]),
  falseTokens: z
    .array(z.string().min(1))
    .min(1)
    .default([...DEFAULT_COLUMN_FORMAT.falseTokens]),
  multiSelectSeparator: z
    .string()
    .min(1)
    .max(4)
    .default(DEFAULT_COLUMN_FORMAT.multiSelectSeparator),
  relationSeparator: z.string().min(1).max(4).default(DEFAULT_COLUMN_FORMAT.relationSeparator),
  currencyCode: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .nullable()
    .default(null),
  phoneRegion: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .nullable()
    .default(null),
});

/** `POST /api/imports`: the draft names an object and nothing else; the file follows. */
export const createImportRequestSchema = z.strictObject({
  objectMetadataId: z.uuid(),
});

/** One file column and what it targets. A field claimed by an earlier column is gone (D6). */
export const importColumnMappingSchema = z.strictObject({
  columnIndex: z
    .int()
    .min(0)
    .max(CSV_MAX_COLUMNS - 1),
  /** Null leaves the column UNMAPPED, which is ignored rather than fatal (D6). */
  targetFieldId: z.uuid().nullable(),
  format: columnFormatSchema.optional(),
});

/**
 * `PATCH /api/imports/:id` carries the run's CONFIGURATION only - mode, match key,
 * per-column target and format - which is bounded by the column count and stays in
 * kilobytes at the cap. The review edits do NOT ride along: D11's 500 distinct values
 * across 200 columns is tens of thousands of entries and past the global 1 MB body limit,
 * on the one route the wizard cannot finish without. They travel on their own route (D43).
 */
export const updateImportRequestSchema = z.strictObject({
  mode: importModeSchema.optional(),
  matchKeyFieldId: z.uuid().nullable().optional(),
  columns: z.array(importColumnMappingSchema).max(CSV_MAX_COLUMNS).optional(),
});

export type UpdateImportRequest = z.infer<typeof updateImportRequestSchema>;

/**
 * One reviewed value and what to do with it (D11, D43). `replacement: null` SKIPS every
 * row holding this value, which is Attio's semantics adopted in behaviour: an unfixed
 * invalid value is skipped, and a record whose mapped values are all invalid or blank is
 * not imported at all.
 */
export const importValueFixSchema = z.strictObject({
  rawValue: z.string(),
  replacement: z.string().nullable(),
});

export type ImportValueFix = z.infer<typeof importValueFixSchema>;

/**
 * `POST /api/imports/:id/columns/:columnIndex/value-fixes` - ONE column per request, so
 * the payload is bounded by D11's cap rather than by the file, and a failed request loses
 * one column's edits rather than the whole review pass.
 */
export const importValueFixesRequestSchema = z.strictObject({
  fixes: z.array(importValueFixSchema).max(IMPORT_REVIEW_DISTINCT_VALUE_CAP),
});

/**
 * THE EXPORT SCOPE IS THE QUERY THE SCREEN IS SHOWING, NOT A SAVED VIEW ID (D32).
 *
 * That removes Attio's published caveat structurally - "Save for everyone before
 * exporting" is a workaround for a server that can only see persisted view state, and
 * ours does not have to be one. INV-6 holds and is stated rather than assumed: the same
 * `RecordQuery` goes through the same `parseRecordQuery` and the same compiler every
 * other read uses, so this decision creates no second evaluator and no second SQL path.
 */
export const createExportRequestSchema = z.strictObject({
  /** Validated by `parseRecordQuery`, which runs its depth bound BEFORE zod. */
  query: z.unknown(),
  /**
   * A selection NARROWS the export. The ids are a query-shaping input BESIDE the
   * `RecordQuery`, never inside its `filter`, and they are applied as an explicit
   * `id in (...)` conjunct at the record-store boundary - the same separation
   * `roles-and-permissions.md` D10 makes for the scope predicate, and for the same
   * reason: a caller-supplied list the compiler must not confuse with an authored filter.
   */
  recordIds: z.array(z.uuid()).max(EXPORT_SELECTION_MAX).optional(),
  /** "All fields on this object" is a second explicit choice, defaulting off (D32). */
  allFields: z.boolean().default(false),
});

export interface CreateExportRequest {
  readonly query: RecordQuery;
  readonly recordIds?: readonly string[] | undefined;
  readonly allFields: boolean;
}

/**
 * The envelope, then the query through the existing parser. Embedding `recordQuerySchema`
 * directly would skip the raw-body depth bound `parseRecordQuery` runs first, and that
 * bound exists because the overflow happens inside zod's own `z.lazy` descent and would
 * surface as a 500 instead of a 400.
 */
export function parseCreateExportRequest(input: unknown): CreateExportRequest {
  const envelope = createExportRequestSchema.parse(input);
  return {
    query: parseRecordQuery(envelope.query),
    recordIds: envelope.recordIds,
    allFields: envelope.allFields,
  };
}
